import { useMutation } from "@tanstack/react-query"
import { sendSupportMsg } from "api/sendSupportMsg"
import Button from "components/Form/Button"
import InputNew from "components/Form/InputNew"
import TextArea from "components/Form/TextArea"
import PhoneInput from "components/atoms/PhoneInput/PhoneInput"
import { useUserContext } from "context/UserContext"
import { useLang } from "hooks/useLang"
import successAnimation from "lottiefiles/success_icon.json"
import dynamic from "next/dynamic"
import { validations } from "validation/contactUs"
import useFormValidation from "validation/hook/useFormValidation"
import CustomSelect from "components/Form/CustomSelect"

const Lottie = dynamic(() => import("lottie-react"), {
    ssr: false,
})

interface IForm {
    first_name: string
    last_name: string
    email: string
    message: string
    phone: string
    calling_code: string
    title: string
    event_id: string
}

interface IProps {
    events?: { en_title: string; ar_title: string; _id: string }[]
}

export default function Form({ events }: IProps) {
    const { t, lang } = useLang()
    const { user } = useUserContext()

    const form: IForm = {
        first_name: user ? user.first_name : "",
        last_name: user ? user.last_name : "",
        email: user ? user.contact.email : "",
        message: "",
        phone: user ? user.contact.phone : "",
        calling_code: "966",
        title: "",
        event_id: "",
    }

    const errors: IForm = {
        first_name: "",
        last_name: "",
        email: "",
        message: "",
        phone: "",
        calling_code: "",
        title: "",
        event_id: "",
    }



    const onSubmit = () => {
        mutate(state)
    }

    const { state, setState, stateErrors, setStateErrors, handleSubmit } =
        useFormValidation(form, errors, validations, onSubmit)

    const onChange = (e: { target: { name: string; value: string } }) => {
        const { name, value } = e.target
        setState((prev) => ({ ...prev, [name]: value }))
        setStateErrors((prev) => ({ ...prev, [name]: "" }))
    }

    const onChangePhone = (name: "phone" | "calling_code", value: string) => {
        setState((prev) => ({ ...prev, [name]: value }))
        setStateErrors((prev) => ({ ...prev, [name]: "" }))
    }

    const onSelectChange = (selectedOption: {
        label: string
        value: string
    }) => {
        setState((prev) => ({ ...prev, event_id: selectedOption.value }))
        setStateErrors((prev) => ({ ...prev, event_id: "" }))
    }

    const options = Array.isArray(events)
        ? events.map((event) => ({
            label: lang === "ar" ? event.ar_title : event.en_title, // Use ar_title for Arabic, en_title for English
            value: event._id,
        }))
        : []


    const {
        mutate,
        isError: isSubmitError,
        isSuccess,
        isPending,
    } = useMutation({
        mutationFn: sendSupportMsg,
    })


    return (
        <>
            {isSuccess && !isSubmitError ? (
                <div className="flex w-full flex-col items-center justify-center md:w-auto md:flex-1">
                    <Lottie
                        animationData={successAnimation}
                        loop={false}
                        className="h-32 w-32"
                    />
                    <span className="text-sm">
                        {t("msg_send_successfully")}
                    </span>
                </div>
            ) : (
                <form
                    className="flex w-full py-4  flex-1 flex-col gap-2 md:gap-5"
                    onSubmit={handleSubmit}
                >
                    <p>{t("contact_us_desc")}</p>

                    <div className="grid mt-2 grid-cols-1 gap-2 md:grid-cols-2 md:gap-5">
                        <InputNew
                            onChange={onChange}
                            inputID="contact_first_name"
                            type={"text"}
                            label={t("lb_First_Name")}
                            value={state.first_name}
                            error={stateErrors.first_name}
                            name={"first_name"}
                            reqStar
                        />
                        <InputNew
                            onChange={onChange}
                            inputID="contact_last_name"
                            type={"text"}
                            label={t("lb_Last_Name")}
                            value={state.last_name}
                            error={stateErrors.last_name}
                            name="last_name"
                            reqStar
                        />
                        <InputNew
                            onChange={onChange}
                            inputID="contact_email"
                            type={"text"}
                            label={t("lb_email")}
                            value={state.email}
                            error={stateErrors.email}
                            name="email"
                            reqStar
                        />
                        <PhoneInput
                            callingCode={state.calling_code}
                            setCallingCode={(callingCode: string) =>
                                onChangePhone("calling_code", callingCode)
                            }
                            setPhone={(phone: string) =>
                                onChangePhone("phone", phone)
                            }
                            phone={state.phone}
                            error={stateErrors.phone}
                            required
                        />
                    </div>
                    <InputNew
                        onChange={onChange}
                        inputID="contact_title"
                        type={"text"}
                        label={t("subject")}
                        value={state.title}
                        error={stateErrors.title}
                        name="title"
                        reqStar
                    />
                    {events && events.length && (
                        <CustomSelect
                            label={t("event")}
                            options={options}
                            onChange={onSelectChange}
                        />
                    )}
                    <TextArea
                        rows={4}
                        onChange={onChange}
                        inputID="contact_message"
                        name={"message"}
                        label={t("message")}
                        value={state.message}
                        error={stateErrors.message}
                        maxLength={200}
                        reqStar
                    />
                    <Button
                        type="submit"
                        className="w-full p-2 md:w-[250px]"
                        text={t("send")}
                        loading={isPending}
                    />
                </form>
            )}
        </>
    )
}
